// ====================================================================================
// WARD OVERVIEW - V.17 "LOCAL ISSUES INTEGRATION"
//
// KEY CHANGES:
// 1. [NEW] - Added 'Local Issues' Tab.
// 2. [INTEGRATION] - Imported renderLocalIssues from WardLocalIssues.jsx.
// 3. [FIX] - Added predictionData prop to enable Oracle visualization
// ====================================================================================
import {
   Globe, ShieldCheck, Users, MessageSquare, Gauge, UserCheck, AlertCircle
} from "lucide-react";
import { useState } from "react";
import { motion } from 'framer-motion';

import { renderOverview } from "./Tabs/Overview";
import { renderElectorate } from "./Tabs/Electorate/Electorate";
import { renderMessaging } from "./Tabs/Messaging/Messaging";
import { renderCandidate } from "./Tabs/WardCandidate"; 
import { renderLocalIssues } from "./Tabs/WardLocalIssues"; 

// import Constants and Helpers that weren't part of tabs
import {formatStrategicTier} from "./Constants";

// import Components written to help show the data
import { ValueBadge } from "./UtilityComponents";


// ====================================================================================
//
//      MAIN WARD OVERVIEW COMPONENT (v.17 - TABBED)
//
// ====================================================================================

export const WardOverview = ({ selectedWard, setDrawerData, candidateProfilerData, localIssues, predictionData }) => {
    // STATE FOR TABS
    const [activeTab, setActiveTab] = useState('overview');
    const [activeElectorateSubTab, setActiveElectorateSubTab] = useState('archetypes');

    if (!selectedWard) {
      return (
        <div className="p-8">
          <div className="flex flex-col items-center justify-center h-[calc(100vh-180px)] text-slate-400">
            <Globe size={48} className="text-slate-600 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Select a Ward from the Sidebar</h2>
            <p>Choose a region and authority to drill down into ward-level data.</p>
          </div>
        </div>
      );
    }

    const currentData = selectedWard;
    
    // --- Data Preparation for Syntheses ---

    // START NCP RANKING MODIFICATION: (Using NCP = Pop * Persuadability * (1 - Activation))
    const priorityArchetypes = (currentData?.archetypes || []).sort((a, b) => {
      // Get the core metrics. Use defaults of 0 for safety.
      const popB = b?.ward_representation_percentage || 0;
      const persB = b?.campaign_targeting?.persuadability_score || 0;
      const actB = b?.campaign_targeting?.activation_difficulty || 0;
      
      const popA = a?.ward_representation_percentage || 0;
      const persA = a?.campaign_targeting?.persuadability_score || 0;
      const actA = a?.campaign_targeting?.activation_difficulty || 0;

      // NCP Score = (Population % * Persuadability Score) * (1 - Activation Difficulty)
      const ncpScoreB = (popB * persB) * (1 - actB);
      const ncpScoreA = (popA * persA) * (1 - actA);
      
      return ncpScoreB - ncpScoreA; // Sort descending (highest NCP first)
    });
    // END NCP RANKING MODIFICATION

    const primaryPersuasionTargetName = priorityArchetypes[0]?.name;

    const squeezeData = currentData?.voter_targeting?.squeeze_opportunities || {};
    const topSqueezeTargets = Object.entries(squeezeData.Top_Squeeze_Target_Parties_THEIA || {})
      .sort(([, a], [, b]) => b - a);
      
    const channelPriorities = Object.entries(currentData?.recommendations?.resources?.channel_priorities || {})
      .sort(([, a], [, b]) => b - a);
      
    const topIssues = Object.entries(currentData?.voter_targeting?.messaging_priorities?.calculated_issue_scores || {})
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    const tier = currentData?.strategic_priority?.tier;
    const tierLabel = formatStrategicTier(tier);

    // --- SYNTHESIS #6: "Ward DNA" Data ---
    const demoSummary = currentData?.demographic_summary || {};
    const wardDNA = {
      location: demoSummary.location_classification?.replace(/_/g, ' ') || 'N/A',
      socialGrade: demoSummary.predominant_social_grade || 'N/A',
      housing: (demoSummary.owner_occupied_pct || 0) > 0.7 ? "High Owner-Occupancy" :
               (demoSummary.owner_occupied_pct || 0) > 0.5 ? "Majority Owner-Occupied" : "Mixed Housing",
      diversity: (demoSummary.ethnic_diversity_index || 0) < 0.2 ? "Low Diversity" :
                 (demoSummary.ethnic_diversity_index || 0) < 0.4 ? "Moderate Diversity" : "High Diversity",
      economy: (demoSummary.economic_precarity_index || 0) < 0.3 ? "Economically Secure" :
               (demoSummary.economic_precarity_index || 0) < 0.5 ? "Economically Stable" : "Economically Precarious",
    };

    // --- SYNTHESIS #7: "Strategic Insight" Logic ---
    let showMessageConflict = false;
    let conflictDetails = { persuasion: [], squeeze: [] };
    
    try {
      if (topSqueezeTargets.length > 0 && priorityArchetypes.length > 0) {
        // 1. Get main persuasion issues
        const persuasionIssues = Object.keys(currentData?.voter_targeting?.messaging_priorities?.calculated_issue_scores || {}).sort((a, b) => 
          currentData.voter_targeting.messaging_priorities.calculated_issue_scores[b] - currentData.voter_targeting.messaging_priorities.calculated_issue_scores[a]
        ).slice(0, 3);
        
        // 2. Get top squeeze party
        const topSqueezeParty = topSqueezeTargets[0][0]; // e.g., "reform"
        
        // 3. Find the archetype that is the source of these votes
        const squeezeArchetype = priorityArchetypes.reduce((max, arch) => 
          (arch?.political_profile?.party_propensity?.[topSqueezeParty] || 0) > (max?.political_profile?.party_propensity?.[topSqueezeParty] || 0) ? arch : max, 
          priorityArchetypes[0]
        );
        
        if (squeezeArchetype) {
          // 4. Get that archetype's issues
          const squeezeIssues = Object.keys(squeezeArchetype?.political_profile?.issue_priorities || {}).sort((a, b) => 
            squeezeArchetype.political_profile.issue_priorities[b] - squeezeArchetype.political_profile.issue_priorities[a]
          ).slice(0, 3);
          
          // 5. Compare the lists
          const overlap = persuasionIssues.filter(issue => squeezeIssues.includes(issue)).length;
          
          // 6. If overlap is low, flag the conflict
          if (overlap < 2) {
            showMessageConflict = true;
            conflictDetails.persuasion = persuasionIssues;
            conflictDetails.squeeze = squeezeIssues;
          }
        }
      }
    } catch (e) {
      console.error("Error in strategic conflict synthesis:", e);
    }
    
    const narrativeData = currentData?.narrative;

    // --- Main Component Return ---

    return (
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }}
        className="p-6 lg:p-10 font-sans"
      >
        
        {/* HEADER (FIXED / GLOBAL) */}
        <header className="mb-6 border-b border-slate-700 pb-6">
          <div className="flex flex-col md:flex-row items-start justify-between">
            <div>
              <h2 className="text-4xl font-bold text-white mb-2">{currentData?.ward_name || 'N/A'}</h2>
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                <ValueBadge value={tierLabel} />
                <div className="text-slate-400 text-sm flex items-center space-x-2">
                  <ShieldCheck size={14} />
                  <span>Strategy: <span className="text-white font-medium">{currentData?.strategic_priority?.campaign_strategy?.replace(/_/g, ' ') || 'N/A'}</span></span>
                </div>
                <div className="text-slate-400 text-sm">
                  Resource: <span className="text-white font-medium">{currentData?.strategic_priority?.resource_allocation || 'N/A'}</span>
                </div>
              </div>
            </div>
            <div className="text-left md:text-right mt-4 md:mt-0">
              <div className="text-3xl font-bold text-accent-400">{currentData?.strategic_priority?.priority_score?.toFixed(1) || '0.0'}</div>
              <div className="text-slate-350 text-sm">Strategic Priority Score</div>
            </div>
          </div>
        </header>

       {/* HIGH-TECH TAB NAVIGATION */}
        <div className="flex items-center space-x-1 md:space-x-8 mb-8 border-b border-slate-700/50 relative overflow-x-auto">
            {[
                { id: 'overview', label: 'Mission Control', icon: Gauge },
                { id: 'electorate', label: 'Electorate', icon: Users },
                { id: 'messaging', label: 'Messaging Engine', icon: MessageSquare },
                { id: 'local_issues', label: 'Local Issues', icon: AlertCircle }, 
                { id: 'candidate', label: 'Candidate', icon: UserCheck }
            ].map((tab) => {
                const isActive = activeTab === tab.id;
                const Icon = tab.icon;
                
                return (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`
                            relative py-4 px-2 flex items-center space-x-2 text-xs md:text-sm font-bold uppercase tracking-widest transition-all duration-300 outline-none group whitespace-nowrap
                            ${isActive ? 'text-white' : 'text-slate-500 hover:text-slate-300'}
                        `}
                    >
                        <Icon 
                            size={16} 
                            className={`transition-all duration-300 ${isActive ? "text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]" : "group-hover:text-slate-300"}`} 
                        />
                        <span className={`hidden md:block ${isActive ? "drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]" : ""}`}>
                            {tab.label}
                        </span>
                         <span className={`md:hidden ${isActive ? "drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]" : ""}`}>
                            {tab.label === 'Mission Control' ? 'Overview' : tab.label.split(' ')[0]}
                        </span>
                        {isActive && (
                            <motion.div 
                                layoutId="activeTab"
                                className="absolute bottom-0 left-0 right-0 h-[2px] bg-white shadow-[0_0_10px_1px_rgba(255,255,255,0.5)]"
                                initial={false}
                                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                            />
                        )}
                        {isActive && (
                           <div className="absolute inset-0 bg-gradient-to-t from-white/5 to-transparent opacity-20 blur-md -z-10" /> 
                        )}
                    </button>
                );
            })}
        </div>

        {/* TAB CONTENT AREA (FULL WIDTH) */}
        <main>
            {activeTab === 'overview' && renderOverview({
                currentData, 
                showMessageConflict, 
                squeezeData, 
                topSqueezeTargets, 
                conflictDetails
            })}
            
            {activeTab === 'electorate' && renderElectorate({
                currentData, 
                wardDNA, 
                activeElectorateSubTab, 
                setActiveElectorateSubTab, 
                priorityArchetypes, 
                topIssues, 
                primaryPersuasionTargetName, 
                setDrawerData 
            })}

            {activeTab === 'messaging' && renderMessaging({
              currentData, 
              narrativeData, 
              channelPriorities,
              setDrawerData, 
              priorityArchetypes
            })}
            
            {activeTab === 'local_issues' && renderLocalIssues({ issues: currentData?.local_issues })}

            {activeTab === 'candidate' && renderCandidate({currentWard: currentData, candidateData: candidateProfilerData})}
        </main>

      </motion.div>
    );
  };